import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Scale, Upload, Loader2, ArrowLeft, Link2, Plus, EyeOff, Undo2, Check, Clock } from "lucide-react";
import { PageHead, Stat, Empty, Field, inputCls, selectCls, btnPrimary, btnGhost, fmtDate } from "@/components/erp/ErpUi";
import { CATEGORY_LABEL } from "@/pages/OwnerFinance";

const STATUS_STYLE = {
  conciliado: ["bg-green-brand/10 text-green-300 border-green-brand/40", "Conciliado"],
  pendente: ["bg-amber-950/40 text-amber-300 border-amber-800", "Pendente"],
  ignorado: ["bg-zinc-900 text-zinc-400 border-zinc-700", "Ignorado"],
};

function CreateDialog({ open, onOpenChange, line, onDone }) {
  const [f, setF] = useState({ category: "", description: "" });
  const [busy, setBusy] = useState(false);
  const cats = useMemo(() => line?.kind === "pagar" ? ["aluguel", "salarios", "comissoes", "impostos", "pecas", "terceiros", "outros"] : ["outros", "os"], [line?.kind]);
  useEffect(() => { if (open && line) setF({ category: cats[0], description: line.memo || "" }); }, [open, line, cats]);
  if (!line) return null;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/erp/reconcile/lines/${line.id}/create`, { kind: line.kind, category: f.category, description: f.description });
      toast.success("Lançamento criado e conciliado"); onDone(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="recon-create-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-display uppercase">Criar lançamento ({line.kind === "pagar" ? "a pagar" : "a receber"})</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="text-xs text-zinc-400">{fmtDate(line.date)} · {brl(line.amount)} · {line.memo || "sem descrição"}</div>
          <Field label="Categoria">
            <select data-testid="recon-create-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={selectCls}>
              {cats.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
            </select>
          </Field>
          <Field label="Descrição"><Input data-testid="recon-create-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} /></Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="recon-create-save" type="submit" disabled={busy || !f.description} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar e conciliar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Line({ ln, onChanged, openCreate }) {
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(ln.candidates?.[0]?.entry_id || "");
  const act = async (p, ok) => { setBusy(true); try { await p; toast.success(ok); onChanged(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const [cls, label] = STATUS_STYLE[ln.status] || STATUS_STYLE.pendente;
  const sign = ln.kind === "receber" ? "text-green-neon" : "text-red-300";
  return (
    <li data-testid={`recon-line-${ln.id}`} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm text-zinc-100 truncate">{ln.memo || "(sem descrição)"}</div>
          <div className="text-[11px] text-zinc-500">{fmtDate(ln.date)} · {ln.kind === "receber" ? "Entrada" : "Saída"}{ln.trntype ? ` · ${ln.trntype}` : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`font-display font-black whitespace-nowrap ${sign}`}>{brl(ln.amount)}</div>
          <span data-testid={`recon-line-status-${ln.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-bold ${cls}`}>{label}</span>
        </div>
      </div>
      {ln.linked?.length > 0 && (
        <div className="mt-2 text-[11px] text-zinc-400">
          Ligado a: {ln.linked.map((e) => <span key={e.id} className="inline-block mr-2">#{e.id.slice(-4)} {e.description} ({brl(e.amount)}, {fmtDate(e.due_date)})</span>)}
          {ln.note && <span className="text-zinc-500"> · {ln.note}</span>}
        </div>
      )}
      {ln.status === "pendente" && (
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          {ln.candidates?.length ? (
            <>
              <select data-testid={`recon-line-pick-${ln.id}`} value={pick} onChange={(e) => setPick(e.target.value)} className={`${selectCls} w-auto max-w-full`}>
                {ln.candidates.map((c) => <option key={c.entry_id} value={c.entry_id}>{c.description} · {brl(c.amount)} · venc {fmtDate(c.due_date)} ({c.score === 0 ? "mesmo dia" : `${c.score}d`})</option>)}
              </select>
              <Button data-testid={`recon-line-link-${ln.id}`} size="sm" disabled={busy || !pick} className={`h-8 text-xs ${btnPrimary}`} onClick={() => act(api.post(`/erp/reconcile/lines/${ln.id}/link`, { entry_id: pick }), "Ligado e dado baixa")}><Link2 className="h-3.5 w-3.5 mr-1" /> Ligar</Button>
            </>
          ) : <span className="text-[11px] text-zinc-500">Nenhum candidato encontrado em ±3 dias</span>}
          <Button data-testid={`recon-line-create-${ln.id}`} size="sm" variant="outline" className={`h-8 text-xs ${btnGhost}`} onClick={() => openCreate(ln)}><Plus className="h-3.5 w-3.5 mr-1" /> Criar lançamento</Button>
          <Button data-testid={`recon-line-ignore-${ln.id}`} size="sm" variant="outline" className={`h-8 text-xs ${btnGhost}`} onClick={() => act(api.post(`/erp/reconcile/lines/${ln.id}/ignore`), "Linha ignorada")}><EyeOff className="h-3.5 w-3.5 mr-1" /> Ignorar</Button>
        </div>
      )}
      {ln.status !== "pendente" && (
        <div className="mt-2">
          <Button data-testid={`recon-line-undo-${ln.id}`} size="sm" variant="outline" className={`h-8 text-xs ${btnGhost}`} onClick={() => act(api.post(`/erp/reconcile/lines/${ln.id}/undo`), "Conciliação desfeita")}><Undo2 className="h-3.5 w-3.5 mr-1" /> Desfazer</Button>
        </div>
      )}
    </li>
  );
}

function SessionsList({ sessions, onImport, uploading }) {
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <label className={`${btnPrimary} h-10 inline-flex items-center cursor-pointer`} data-testid="recon-import-label">
          {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Importar extrato OFX
          <input data-testid="recon-import-input" type="file" accept=".ofx,.OFX,application/x-ofx,text/plain" className="hidden" onChange={onImport} disabled={uploading} />
        </label>
      </div>
      {sessions === null ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" />
        : sessions.length === 0 ? <Empty>Nenhum extrato importado ainda. Baixe o OFX do seu banco e importe aqui.</Empty>
        : <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.id} data-testid={`recon-session-${s.id}`} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
                <Link to={`/owner/conciliar/${s.id}`} className="block">
                  <div className="text-sm font-bold text-zinc-100">{s.filename}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {new Date(s.created_at).toLocaleString("pt-BR")} · banco {s.bank_name || s.bank_id || "?"} · conta {s.acct_id || "?"} · {s.total} linhas
                  </div>
                  <div className="mt-1 flex gap-3 text-[11px]">
                    <span className="text-green-neon"><Check className="inline h-3 w-3" /> auto {s.counts?.auto || 0}</span>
                    <span className="text-amber-300"><Clock className="inline h-3 w-3" /> pendentes {s.counts?.pending || 0}</span>
                    <span className="text-zinc-500">duplicadas {s.counts?.duplicated || 0}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>}
    </div>
  );
}

function SessionDetail({ id, onImport, uploading }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [create, setCreate] = useState(null);
  const load = useCallback(() => api.get(`/erp/reconcile/sessions/${id}`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const c = data?.counts;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button data-testid="recon-back" variant="outline" className={btnGhost} onClick={() => navigate("/owner/conciliar")}><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
        <label className={`${btnGhost} h-9 px-3 inline-flex items-center cursor-pointer border rounded-md`}>
          {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Importar outro
          <input type="file" accept=".ofx,.OFX" className="hidden" onChange={onImport} disabled={uploading} />
        </label>
      </div>
      {data && <div className="text-xs text-zinc-500">{data.filename} · banco {data.bank_name || data.bank_id || "?"} · conta {data.acct_id || "?"}</div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="recon-count-auto" label="Conciliado auto" value={c?.auto ?? "…"} tone="green" />
        <Stat testid="recon-count-created" label="Lançado agora" value={c?.created ?? "…"} />
        <Stat testid="recon-count-pending" label="Pendentes" value={c?.pending ?? "…"} tone="amber" />
        <Stat testid="recon-count-ignored" label="Ignorados" value={c?.ignored ?? "…"} />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> :
        <ul className="space-y-2">{data.lines.map((ln) => <Line key={ln.id} ln={ln} onChanged={load} openCreate={setCreate} />)}</ul>}
      <CreateDialog open={!!create} onOpenChange={(o) => !o && setCreate(null)} line={create} onDone={load} />
    </div>
  );
}

export default function OwnerReconcile() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [uploading, setUploading] = useState(false);
  const loadSessions = useCallback(() => api.get("/erp/reconcile/sessions").then(({ data }) => setSessions(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { if (!sessionId) loadSessions(); }, [sessionId, loadSessions]);
  const onImport = async (ev) => {
    const file = ev.target.files?.[0]; ev.target.value = ""; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/erp/reconcile/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.counts.auto} conciliadas, ${data.counts.pending} pendentes${data.counts.duplicated ? `, ${data.counts.duplicated} duplicadas` : ""}`);
      navigate(`/owner/conciliar/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={Scale} kicker="Financeiro" title="Conciliação bancária" />
      {sessionId ? <SessionDetail id={sessionId} onImport={onImport} uploading={uploading} />
        : <SessionsList sessions={sessions} onImport={onImport} uploading={uploading} />}
    </div>
  );
}
